/**
 * How a character looks, chosen in the character creator (ui/creator.ts) and kept in the save
 * (`PlayerProgress.appearance`): sex, face, hair and its colour, skin, eyes, and the body's shape.
 * Every field is an id from the lists below (or a number on a body axis), so a save or a message
 * from the network can be checked against them (`parseAppearance`). Data only: the 3D look of each
 * id is the client's (render3d/characters/hero/appearance.ts), and the body's morphs are
 * render3d/characters/body.ts.
 */

export type Sex = 'm' | 'f';

/** The body's shape, each axis −1…1 (0 = Model 0's own shape). */
export interface BodyShape {
  height: number;
  build: number;
  shoulders: number;
  chest: number;
  waist: number;
  hips: number;
  legs: number;
  arms: number;
  head: number;
}

export interface Appearance {
  sex: Sex;
  face: string;
  hair: string;
  hairColor: string;
  skin: string;
  eyes: string;
  body: BodyShape;
}

export const BODY_AXES = [
  'height',
  'build',
  'shoulders',
  'chest',
  'waist',
  'hips',
  'legs',
  'arms',
  'head',
] as const satisfies readonly (keyof BodyShape)[];

/** Each axis's label and the words for its two ends (the creator's sliders). */
export const BODY_LABELS: Readonly<Record<keyof BodyShape, { label: string; low: string; high: string }>> = {
  height: { label: 'ส่วนสูง', low: 'เตี้ย', high: 'สูง' },
  build: { label: 'รูปร่าง', low: 'ผอมบาง', high: 'กำยำ' },
  shoulders: { label: 'ไหล่', low: 'แคบ', high: 'กว้าง' },
  chest: { label: 'อก', low: 'แบน', high: 'อวบ' },
  waist: { label: 'เอว', low: 'คอด', high: 'หนา' },
  hips: { label: 'สะโพก', low: 'แคบ', high: 'ผาย' },
  legs: { label: 'ช่วงขา', low: 'สั้น', high: 'ยาว' },
  arms: { label: 'ช่วงแขน', low: 'สั้น', high: 'ยาว' },
  head: { label: 'ขนาดศีรษะ', low: 'เล็ก', high: 'ใหญ่' },
};

export const SEX_LABELS: Readonly<Record<Sex, string>> = { m: 'ชาย', f: 'หญิง' };

/** A choice: its id, its Thai label, and the sexes it fits (both when left out). */
export interface LookChoice {
  readonly id: string;
  readonly label: string;
  readonly sex?: Sex;
}

/** A colour choice: its swatch ('#rrggbb'), what the 3D look tints to. */
export interface ColourChoice extends LookChoice {
  readonly hex: string;
}

/**
 * Faces: Model 0's face of each sex, worn with a resting look (how the brows and eyes sit at rest).
 * More face shapes need their own heads (see the creator's report).
 */
export const FACES: readonly LookChoice[] = [
  { id: 'calm', label: 'สุขุม' },
  { id: 'stern', label: 'เคร่งขรึม' },
  { id: 'gentle', label: 'อ่อนโยน' },
  { id: 'cold', label: 'เย็นชา' },
  { id: 'bright', label: 'สดใส' },
  { id: 'sly', label: 'เจ้าเล่ห์' },
];

/** Hair styles: the ones built today, one per sex (Model 0's own). */
export const HAIRS: readonly LookChoice[] = [
  { id: 'ponytail', label: 'หางม้าผูกริบบิ้น', sex: 'm' },
  { id: 'longTied', label: 'ผมยาวมวยครึ่งศีรษะ', sex: 'f' },
];

/** Hair colours (the hair's main tone); the first is Model 0's own ink-brown. */
export const HAIR_COLORS: readonly ColourChoice[] = [
  { id: 'ink', label: 'ดำหมึก', hex: '#3a2423' },
  { id: 'jet', label: 'ดำขลับ', hex: '#1e1c22' },
  { id: 'brown', label: 'น้ำตาลเข้ม', hex: '#5a3626' },
  { id: 'chestnut', label: 'น้ำตาลเกาลัด', hex: '#8a5236' },
  { id: 'wine', label: 'แดงไวน์', hex: '#6a2232' },
  { id: 'indigo', label: 'ครามเข้ม', hex: '#262c4e' },
  { id: 'ash', label: 'เทาเถ้า', hex: '#8a827c' },
  { id: 'silver', label: 'ขาวเงิน', hex: '#d8d4d0' },
];

/** Skin tones; the first is Model 0's own (hero/contract.py SKIN). */
export const SKIN_TONES: readonly ColourChoice[] = [
  { id: 'warm', label: 'ผิวอบอุ่น', hex: '#eabba3' },
  { id: 'fair', label: 'ผิวขาวผ่อง', hex: '#f4cfbc' },
  { id: 'ivory', label: 'ผิวงาช้าง', hex: '#f0d2b4' },
  { id: 'wheat', label: 'ผิวสีน้ำผึ้ง', hex: '#d9a27c' },
  { id: 'tan', label: 'ผิวแทน', hex: '#c08560' },
  { id: 'bronze', label: 'ผิวคล้ำ', hex: '#93603f' },
];

/** Eye colours; the first is Model 0's own warm amber-brown. */
export const EYE_COLORS: readonly ColourChoice[] = [
  { id: 'amber', label: 'น้ำตาลอำพัน', hex: '#7d4c35' },
  { id: 'dark', label: 'ดำสนิท', hex: '#2a1c1c' },
  { id: 'hazel', label: 'น้ำตาลอ่อน', hex: '#9a6a3a' },
  { id: 'jade', label: 'เขียวหยก', hex: '#3d7a5c' },
  { id: 'grey', label: 'เทาหมอก', hex: '#6a7484' },
  { id: 'crimson', label: 'แดงชาด', hex: '#9a2a2a' },
  { id: 'gold', label: 'ทองคำ', hex: '#c89a3a' },
];

export const neutralBody = (): BodyShape => ({
  height: 0,
  build: 0,
  shoulders: 0,
  chest: 0,
  waist: 0,
  hips: 0,
  legs: 0,
  arms: 0,
  head: 0,
});

const fits = (c: LookChoice, sex: Sex): boolean => !c.sex || c.sex === sex;

/** The choices of a list that fit a sex. */
export const choicesFor = <T extends LookChoice>(list: readonly T[], sex: Sex): T[] =>
  list.filter((c) => fits(c, sex));

/** Model 0 of a sex as built: its own face, hair and colours, the body as modelled. */
export function defaultAppearance(sex: Sex = 'm'): Appearance {
  return {
    sex,
    face: FACES[0]!.id,
    hair: choicesFor(HAIRS, sex)[0]!.id,
    hairColor: HAIR_COLORS[0]!.id,
    skin: SKIN_TONES[0]!.id,
    eyes: EYE_COLORS[0]!.id,
    body: neutralBody(),
  };
}

/** A preset: a name and a whole look. */
export interface AppearancePreset {
  readonly id: string;
  readonly label: string;
  readonly look: Appearance;
}

const preset = (
  id: string,
  label: string,
  sex: Sex,
  o: Partial<Omit<Appearance, 'sex' | 'body'>>,
  body: Partial<BodyShape> = {},
): AppearancePreset => ({
  id,
  label,
  look: { ...defaultAppearance(sex), ...o, body: { ...neutralBody(), ...body } },
});

/** Ready-made looks to start from. */
export const APPEARANCE_PRESETS: readonly AppearancePreset[] = [
  preset('swordsman', 'จอมยุทธ์พเนจร', 'm', {}),
  preset(
    'scholar',
    'บัณฑิตกระบี่',
    'm',
    { face: 'gentle', skin: 'fair', eyes: 'dark' },
    {
      build: -0.4,
      shoulders: -0.2,
      height: 0.2,
    },
  ),
  preset(
    'warrior',
    'ขุนศึกชายแดน',
    'm',
    { face: 'stern', hairColor: 'jet', skin: 'tan', eyes: 'dark' },
    {
      build: 0.7,
      shoulders: 0.6,
      chest: 0.4,
      height: 0.4,
      arms: 0.2,
    },
  ),
  preset(
    'hermit',
    'ฤๅษีผมเงิน',
    'm',
    { face: 'cold', hairColor: 'silver', eyes: 'grey' },
    {
      build: -0.3,
      height: 0.1,
    },
  ),
  preset('heroine', 'จอมยุทธ์หญิง', 'f', {}),
  preset(
    'maiden',
    'คุณหนูสำนักใหญ่',
    'f',
    { face: 'bright', skin: 'fair', hairColor: 'brown' },
    {
      height: -0.3,
      build: -0.3,
      head: 0.1,
    },
  ),
  preset(
    'assassin',
    'มือสังหารเงา',
    'f',
    { face: 'cold', hairColor: 'indigo', eyes: 'crimson' },
    {
      height: 0.3,
      build: -0.2,
      waist: -0.4,
      legs: 0.4,
    },
  ),
  preset(
    'wanderer',
    'นางพญาทะเลทราย',
    'f',
    { face: 'sly', hairColor: 'wine', skin: 'wheat', eyes: 'gold' },
    {
      height: 0.2,
      hips: 0.4,
      chest: 0.3,
    },
  ),
];

const clampAxis = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.round(Math.min(1, Math.max(-1, v)) * 1000) / 1000 : 0;

/** A body shape within its limits: each axis clamped to −1…1 (to 0.001), anything else 0. */
export function parseBody(raw: unknown): BodyShape {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out = neutralBody();
  for (const k of BODY_AXES) out[k] = clampAxis(r[k]);
  return out;
}

/**
 * An appearance repaired against the lists: an unknown sex is male (every save before the creator
 * was the man), an id that is not in its list (or does not fit the sex) is the sex's default, and the
 * body is clamped. Never fails: anything that is not an appearance is the default man.
 */
export function parseAppearance(raw: unknown): Appearance {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const sex: Sex = r.sex === 'f' ? 'f' : 'm';
  const d = defaultAppearance(sex);
  const pick = (list: readonly LookChoice[], v: unknown, fallback: string): string =>
    typeof v === 'string' && list.some((c) => c.id === v && fits(c, sex)) ? v : fallback;
  return {
    sex,
    face: pick(FACES, r.face, d.face),
    hair: pick(HAIRS, r.hair, d.hair),
    hairColor: pick(HAIR_COLORS, r.hairColor, d.hairColor),
    skin: pick(SKIN_TONES, r.skin, d.skin),
    eyes: pick(EYE_COLORS, r.eyes, d.eyes),
    body: parseBody(r.body),
  };
}

/** Whether two appearances are the same look. */
export const sameAppearance = (a: Appearance, b: Appearance): boolean =>
  a.sex === b.sex &&
  a.face === b.face &&
  a.hair === b.hair &&
  a.hairColor === b.hairColor &&
  a.skin === b.skin &&
  a.eyes === b.eyes &&
  BODY_AXES.every((k) => a.body[k] === b.body[k]);

/**
 * A random look of a sex, from `rnd` (0 ≤ x < 1; the creator's dice, not the World's Rng). Body
 * axes stay within ±0.6, so a roll is never a caricature.
 */
export function randomAppearance(sex: Sex, rnd: () => number): Appearance {
  const one = <T>(list: readonly T[]): T => list[Math.floor(rnd() * list.length) % list.length]!;
  const body = neutralBody();
  for (const k of BODY_AXES) body[k] = Math.round((rnd() * 2 - 1) * 0.6 * 100) / 100;
  return parseAppearance({
    sex,
    face: one(FACES).id,
    hair: one(choicesFor(HAIRS, sex)).id,
    hairColor: one(HAIR_COLORS).id,
    skin: one(SKIN_TONES).id,
    eyes: one(EYE_COLORS).id,
    body,
  });
}
